import React, { useState, useEffect, ChangeEvent } from 'react';
import { Table, Button, Input, Form, message, Space, Row, Col, Modal } from 'antd';
import apiClient from '../services/api';
import { AssetCategory } from '../types';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import AssetCategoryForm from '../components/AssetCategoryForm';
import { fetchWithLoading } from '../utils/fetchHelper';
import { confirmDelete } from '../utils/confirmDelete';
import { accentInsensitiveIncludes } from '../utils/accentUtils';

const AssetCategoryPage = () => {
  const [form] = Form.useForm();
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState<AssetCategory | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [modalVisible, setModalVisible] = useState(false);

  const fetchCategories = async () => {
    await fetchWithLoading(
      () => apiClient.getAssetCategories(),
      setLoading,
      (data) => setCategories(data as AssetCategory[]),
      'Không thể tải danh mục'
    );
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleEdit = (record: AssetCategory) => {
    setEditingCategory(record);
    form.setFieldsValue({ name: record.name, description: record.description });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.deleteAssetCategory(id);
      message.success('Đã xóa danh mục');
      fetchCategories();
    } catch {
      message.error('Xóa danh mục thất bại!');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingCategory) {
        await apiClient.updateAssetCategory(editingCategory.id, values);
        message.success('Cập nhật danh mục thành công!');
      } else {
        await apiClient.createAssetCategory(values);
        message.success('Tạo danh mục thành công!');
      }
      form.resetFields();
      setEditingCategory(null);
      setModalVisible(false);
      fetchCategories();
    } catch {
      message.error(editingCategory ? 'Cập nhật thất bại!' : 'Tạo danh mục thất bại!');
    }
  };

  const handleDeleteClick = (record: AssetCategory) => {
    confirmDelete('Bạn có chắc muốn xóa danh mục này?', () => handleDelete(record.id));
  };

  const filteredCategories = categories.filter((cat: AssetCategory) =>
    accentInsensitiveIncludes(cat.name, searchValue) ||
    accentInsensitiveIncludes(cat.description || '', searchValue)
  );

  const columns = [
    { title: 'Tên danh mục', dataIndex: 'name', key: 'name', sorter: (a: AssetCategory, b: AssetCategory) => a.name.localeCompare(b.name) },
    { title: 'Mô tả', dataIndex: 'description', key: 'description' },
    {
      title: 'Hành động',
      key: 'action',
      width: 110,
      render: (_: any, record: AssetCategory) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDeleteClick(record)} />
        </Space>
      )
    }
  ];

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16, alignItems: 'center' }}>
        <Col xs={24} md={20}>
          <Input
            className="ams-input"
            placeholder="Tìm kiếm tên danh mục"
            value={searchValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            allowClear
            size="large"
            style={{ width: '100%', borderRadius: 8, height: 40 }}
          />
        </Col>
        <Col xs={24} md={4}>
          <Button className="ams-btn" type="primary" size="large" icon={<EditOutlined />} style={{ borderRadius: 8, height: 40, width: '100%' }} onClick={() => { setEditingCategory(null); form.resetFields(); setModalVisible(true); }}>Thêm mới</Button>
        </Col>
      </Row>
      <Modal
        open={modalVisible}
        title={editingCategory ? 'Cập nhật danh mục' : 'Tạo mới danh mục'}
        onCancel={() => { setModalVisible(false); setEditingCategory(null); form.resetFields(); }}
        footer={null}
      >
        <AssetCategoryForm
          form={form}
          initialValues={editingCategory || {}}
          onFinish={handleSubmit}
          loading={loading}
          submitText={editingCategory ? 'Cập nhật' : 'Tạo mới'}
        />
        {editingCategory && (
          <Button className="ams-btn" style={{ width: '100%', borderRadius: 8, height: 40, marginTop: 8 }} size="large" onClick={() => { setEditingCategory(null); form.resetFields(); setModalVisible(false); }}>Hủy</Button>
        )}
      </Modal>
      <Table
        dataSource={filteredCategories}
        rowKey="id"
        loading={loading}
        columns={columns}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default AssetCategoryPage; 